import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import {
  BackButton,
  SubmitButton,
  ThinDropdownWithSearch,
  ThinTextField,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";

const AddPermissions = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = Yup.object({
    permission_name: Yup.string()
      .required("Permission name is required")
      .min(3, "Permission name must be at least 3 characters")
      .max(50, "Permission name must not exceed 50 characters")
      .matches(/^[^\d]/, "Permission name cannot start with a number")
      .matches(
        /^[a-zA-Z0-9-_]+$/,
        "Only letters, numbers, dashes and underscores allowed"
      ),
    group: Yup.string()
      .required("Group is required")
      .min(2, "Group must be at least 2 characters"),
    status: Yup.number()
      .oneOf([0, 1], "Invalid status")
      .required("Status is required"),
  });

  const formik = useFormik({
    initialValues: {
      permission_name: "",
      group: "",
      status: 1,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const response = await fetchData(
          "permission",
          "POST",
          values,
          {},
          {},
          navigate,
          "/manage-permissions"
        );

        if (response?.success) {
          toast.success("Permission created successfully!");
          formik.resetForm();
          navigate("/manage-permissions");
        } else {
          if (
            response?.message &&
            response.message.toLowerCase().includes("already exists")
          ) {
            formik.setFieldError("permission_name", response.message);
          } else {
            toast.error(
              response?.message ||
                "Failed to add permission. Please try again later."
            );
          }
        }
      } catch (error) {
        console.error("Add permission error:", error);

        const apiMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Error occurred while adding permission.";

        if (apiMessage.toLowerCase().includes("already exists")) {
          formik.setFieldError("permission_name", apiMessage);
        } else {
          toast.error(apiMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Create Permission
        </h1>
        <BackButton to="/manage-permissions" text="Back" />
      </div>

      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Create Permission
          </h1>
        </div>

        <div className="p-4">
          <form onSubmit={formik.handleSubmit}>
            <div className="flex lg:flex-nowrap flex-wrap gap-5">
              <ThinTextField
                id="permission_name"
                label="Permission Name"
                placeHolder="Enter permission name"
                type="text"
                value={formik.values.permission_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.permission_name &&
                  Boolean(formik.errors.permission_name)
                }
                helperText={
                  formik.touched.permission_name &&
                  formik.errors.permission_name
                }
              />

              <ThinTextField
                id="group"
                label="Group"
                placeHolder="Enter group name"
                type="text"
                value={formik.values.group}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.group && Boolean(formik.errors.group)}
                helperText={formik.touched.group && formik.errors.group}
              />

              <ThinDropdownWithSearch
                label="Status"
                id="status-dropdown"
                options={[
                  { value: 1, label: "Active" },
                  { value: 0, label: "In-Active" },
                ]}
                value={formik.values.status}
                onChange={(value) =>
                  formik.setFieldValue("status", Number(value))
                }
                error={formik.touched.status && Boolean(formik.errors.status)}
                helperText={formik.touched.status && formik.errors.status}
              />
            </div>

            <div className="mt-6">
              <SubmitButton
                type="submit"
                text={isSubmitting ? "Submitting..." : "Submit"}
                disabled={isSubmitting}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPermissions;
